import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Download, Loader2, FileCheck2, Image as ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function fetchLogoDataUri(orgId) {
  if (!orgId) return null;
  try {
    const res = await axios.get(`${API_URL}/api/organizations/${orgId}/logo`, {
      withCredentials: true, responseType: 'blob',
    });
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(res.data);
    });
  } catch {
    return null;
  }
}

/**
 * Pre-export options dialog for the Scope Pack.
 * Calls onExport(options) with the user's selections once they click Download.
 */
export function ScopePackOptionsDialog({ open, onOpenChange, estimate, componentCount, onExport }) {
  const [onlyRequired, setOnlyRequired] = useState(false);
  const [includeCoverStats, setIncludeCoverStats] = useState(true);
  const [includePreparedBy, setIncludePreparedBy] = useState(true);
  const [customNote, setCustomNote] = useState('');
  const [coBrand, setCoBrand] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let clientLogoDataUri = null;
      if (coBrand && estimate?.org_id) {
        clientLogoDataUri = await fetchLogoDataUri(estimate.org_id);
        if (!clientLogoDataUri) toast.info('No client logo on file \u2014 exporting without co-branding.');
      }
      await onExport({
        onlyRequired,
        includeCoverStats,
        includePreparedBy,
        customNote: customNote.trim(),
        clientLogoDataUri,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="scope-pack-options-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
            <FileCheck2 className="w-4 h-4" /> Scope Pack options
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Customize the PDF before it downloads. {componentCount} component{componentCount === 1 ? '' : 's'} will be included by default.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={onlyRequired} onChange={e => setOnlyRequired(e.target.checked)}
              className="mt-0.5" data-testid="scope-opt-only-required" />
            <div>
              <div className="text-sm font-medium text-[#0F2040]">Required components only</div>
              <p className="text-[10px] text-slate-500">Exclude optional components. Recommended items are still included.</p>
            </div>
          </label>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={includeCoverStats} onChange={e => setIncludeCoverStats(e.target.checked)}
              className="mt-0.5" data-testid="scope-opt-cover-stats" />
            <div>
              <div className="text-sm font-medium text-[#0F2040]">Include cover stats</div>
              <p className="text-[10px] text-slate-500">Total Effort & Total Cost on the cover page.</p>
            </div>
          </label>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={includePreparedBy} onChange={e => setIncludePreparedBy(e.target.checked)}
              className="mt-0.5" data-testid="scope-opt-prepared-by" />
            <div>
              <div className="text-sm font-medium text-[#0F2040]">Include &quot;Prepared by&quot; signature</div>
              <p className="text-[10px] text-slate-500">Adds your name & email as the author on the cover.</p>
            </div>
          </label>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={coBrand} onChange={e => setCoBrand(e.target.checked)}
              className="mt-0.5" data-testid="scope-opt-cobrand" />
            <div>
              <div className="text-sm font-medium text-[#0F2040] flex items-center gap-1">
                <ImageIcon className="w-3 h-3" /> Co-brand with client logo
              </div>
              <p className="text-[10px] text-slate-500">
                Pulls the logo from the Customer Organization record. Silently skipped if no logo is on file.
              </p>
            </div>
          </label>

          <div>
            <Label className="text-xs font-medium text-[#0F2040]">Custom note (optional)</Label>
            <Textarea value={customNote} onChange={e => setCustomNote(e.target.value)}
              placeholder="e.g. This scope reflects our Nov 3 discovery workshop. Any changes will be tracked as a Change Request."
              rows={3} className="text-xs mt-1" maxLength={400}
              data-testid="scope-opt-note" />
            <p className="text-[10px] text-slate-400 mt-0.5">Rendered on the cover page. {customNote.length}/400.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="scope-pack-cancel">Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={submitting}
            className="bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="scope-pack-confirm">
            {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            Download Pack
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
